import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getLedgerPublicKey,
  listLedgerEvents,
  replayDeployment,
  verifyLedger,
  type ChainVerification,
  type LedgerEvent,
  type PublicKeyInfo,
  type ReplayResult,
} from "../../api";
import { verifyChain, type ChainCheck } from "../../lib/verify";
import { APP_ROUTES } from "../../routes";

/**
 * The evidence pack for one deployment.
 *
 * Everything on this page is read out of signed ledger entries, and every
 * entry is re-verified in the reader's own browser before it is shown as
 * verified. The server's opinion of its own records is displayed too, but
 * labelled as that — the browser check is the one a reader can trust
 * without trusting us.
 *
 * The ordering is deliberate. The first thing shown is what the deployment
 * could NOT do. A record that only ever says flattering things is not
 * evidence of anything; the posture card is what makes the rest of the page
 * worth reading.
 */

// ---------------------------------------------------------------------------
// Shapes of the signed rationale payloads (see platform-api/routes/deployments.py)
// ---------------------------------------------------------------------------

interface Posture {
  robot_type?: string;
  robot_model?: string | null;
  connector_stub?: boolean;
  connected?: boolean;
  force_limiting?: "enforced" | "not_enforceable";
  force_threshold_n?: number | null;
  workspace_rehearsal?: "enforced" | "not_enforceable";
  clamped_fields?: string[];
  policy_kind?: string;
  vla_backend?: string | null;
  summary?: string;
}

interface Channel {
  ticks: number;
  ticks_with_violation: number;
  rate_per_tick: number;
  by_category: Record<string, number>;
  by_joint: Record<string, number>;
  bands: Record<string, number>;
  max_exceedance_ratio: number;
}

interface Intervention {
  model_ambition?: Channel;
  shield_engagement?: Channel;
  held_ticks?: number;
  observe_only?: boolean;
  commands_sent?: number;
  force_exceedances?: number;
  max_force_seen_n?: number;
  telemetry_sha256?: string | null;
  journal_sha256?: string | null;
  note?: string;
}

interface Envelope {
  requested?: Record<string, unknown> | null;
  effective?: {
    joint_limits_rad?: number[][] | null;
    velocity_limits_rad_s?: number[] | null;
    workspace_bounds_m?: number[] | null;
    force_threshold_n?: number | null;
    force_limiting?: string | null;
    workspace_rehearsal?: string | null;
    clamped_fields?: string[];
  } | null;
  authority?: string;
}

function parseRationale<T>(event: LedgerEvent | undefined): T | null {
  if (!event?.rationale) return null;
  try {
    return JSON.parse(event.rationale) as T;
  } catch {
    return null;
  }
}

function latest(events: LedgerEvent[], type: string): LedgerEvent | undefined {
  // events arrive newest first
  return events.find((e) => e.event_type === type);
}

const BAND_LABELS: Array<[string, string]> = [
  ["level_1_marginal", "1–1.5×"],
  ["level_2_moderate", "1.5–3×"],
  ["level_3_severe", "3–10×"],
  ["level_4_extreme", ">10×"],
];

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

type Tone = "ok" | "warn" | "bad" | "muted";

function Statement({
  tone,
  headline,
  children,
}: {
  tone: Tone;
  headline: string;
  children: ReactNode;
}) {
  return (
    <div className={`ev-statement ev-statement-${tone}`}>
      <div className="ev-statement-headline">{headline}</div>
      <div className="ev-statement-body">{children}</div>
    </div>
  );
}

function Hash({ value, short = 12 }: { value?: string | null; short?: number }) {
  if (!value) return <code className="ev-hash">—</code>;
  return (
    <code className="ev-hash" title={value}>
      {value.slice(0, short)}
    </code>
  );
}

function Verified({
  entry,
  check,
}: {
  entry?: LedgerEvent;
  check: ChainCheck | null;
}) {
  if (!entry) return null;
  const row = check?.entries.find((e) => e.entry.id === entry.id);
  if (!check || !row) return <span className="ev-badge ev-badge-muted">checking…</span>;
  if (!row.result.supported) {
    return (
      <span className="ev-badge ev-badge-warn" title={row.result.problems.join("; ")}>
        hash ok · browser cannot check Ed25519
      </span>
    );
  }
  const ok = row.result.hashOk && row.result.signatureOk;
  return (
    <span
      className={`ev-badge ${ok ? "ev-badge-ok" : "ev-badge-bad"}`}
      title={ok ? `entry ${entry.entry_hash}` : row.result.problems.join("; ")}
    >
      {ok ? "verified in this browser" : "FAILED verification"}
    </span>
  );
}

function SignedBy({
  entry,
  check,
}: {
  entry?: LedgerEvent;
  check: ChainCheck | null;
}) {
  if (!entry) return null;
  return (
    <div className="ev-signed">
      <span>
        #{entry.seq} · {entry.actor} ·{" "}
        {entry.created_at ? new Date(entry.created_at).toLocaleString() : "—"}
      </span>
      <Hash value={entry.entry_hash} />
      <Verified entry={entry} check={check} />
    </div>
  );
}

function ChannelCard({
  name,
  subtitle,
  data,
  bandMax,
}: {
  name: string;
  subtitle: string;
  data: Channel;
  bandMax: number;
}) {
  const frac = data.ticks ? data.ticks_with_violation / data.ticks : 0;
  return (
    <div className="ev-channel">
      <div className="ev-channel-name">{name}</div>
      <div className="ev-channel-sub">{subtitle}</div>

      <div className="ev-channel-big">
        {data.max_exceedance_ratio.toFixed(1)}
        <span className="ev-channel-unit">× limit, worst tick</span>
      </div>

      <div className="ev-frac">
        <div className="ev-frac-bar">
          <div className="ev-frac-fill" style={{ width: `${Math.min(100, frac * 100)}%` }} />
        </div>
        <div className="ev-frac-label">
          {data.ticks_with_violation} of {data.ticks} ticks
          <span className="text-muted"> — the denominator is part of the number</span>
        </div>
      </div>

      <div className="ev-bands">
        {BAND_LABELS.map(([key, label]) => {
          const n = data.bands?.[key] ?? 0;
          const h = bandMax ? (n / bandMax) * 100 : 0;
          return (
            <div key={key} className="ev-band">
              <div className="ev-band-col">
                <div
                  className={`ev-band-fill ev-band-${key.split("_")[1]}`}
                  style={{ height: `${h}%` }}
                  title={`${n} exceedances at ${label}`}
                />
              </div>
              <div className="ev-band-label">{label}</div>
              <div className="ev-band-n">{n}</div>
            </div>
          );
        })}
      </div>

      {Object.keys(data.by_category ?? {}).length > 0 && (
        <div className="ev-kv">
          {Object.entries(data.by_category).map(([k, v]) => (
            <span key={k} className="ev-chip">
              {k.replace(/_/g, " ")} {v}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Evidence() {
  const { id: projectId = "", deploymentId = "" } = useParams<{
    id: string;
    deploymentId: string;
  }>();

  const [events, setEvents] = useState<LedgerEvent[]>([]);
  const [key, setKey] = useState<PublicKeyInfo | null>(null);
  const [server, setServer] = useState<ChainVerification | null>(null);
  const [browser, setBrowser] = useState<ChainCheck | null>(null);
  const [replay, setReplay] = useState<ReplayResult | null>(null);
  const [replayError, setReplayError] = useState<string | null>(null);
  const [replaying, setReplaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId || !deploymentId) return;
    let cancelled = false;
    (async () => {
      try {
        const [evts, pk, srv] = await Promise.all([
          listLedgerEvents(projectId, undefined, deploymentId),
          getLedgerPublicKey(),
          verifyLedger(projectId),
        ]);
        if (cancelled) return;
        setEvents(evts);
        setKey(pk);
        setServer(srv);
        // The check that counts runs here, not on the server.
        const check = await verifyChain(evts, pk.public_key);
        if (!cancelled) setBrowser(check);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, deploymentId]);

  const runReplay = useCallback(async () => {
    setReplaying(true);
    setReplayError(null);
    try {
      setReplay(await replayDeployment(deploymentId));
    } catch (e) {
      setReplayError((e as Error).message);
    } finally {
      setReplaying(false);
    }
  }, [deploymentId]);

  const postureEvent = latest(events, "posture.attested");
  const envelopeEvent = latest(events, "envelope.admitted");
  const interventionEvent = latest(events, "intervention.recorded");
  const overrides = events.filter((e) => e.event_type === "envelope.override_unlocked");

  const posture = parseRationale<Posture>(postureEvent);
  const envelope = parseRationale<Envelope>(envelopeEvent);
  const intervention = parseRationale<Intervention>(interventionEvent);

  const verifiedCount = useMemo(
    () =>
      browser
        ? browser.entries.filter((e) => e.result.hashOk && e.result.signatureOk).length
        : 0,
    [browser],
  );

  const bandMax = useMemo(() => {
    const all = [
      ...Object.values(intervention?.model_ambition?.bands ?? {}),
      ...Object.values(intervention?.shield_engagement?.bands ?? {}),
    ];
    return Math.max(1, ...all);
  }, [intervention]);

  const shortId = deploymentId.slice(0, 8);

  return (
    <div className="manifold-page ev-page">
      <header className="manifold-page-header">
        <p className="sg-section-kicker">Evidence pack</p>
        <h1>Deployment {shortId}</h1>
        <p className="text-muted">
          Every statement below is read from a signed ledger entry and
          re-verified in your browser against the published public key. We
          do not get to grade our own record.
        </p>
        <Link to={APP_ROUTES.deploy(projectId)} className="sg-btn sg-btn-ghost">
          ← Deployments
        </Link>
      </header>

      {error && <div className="chain-banner bad">Could not load evidence: {error}</div>}

      {/* Verification status: browser first, server second, labelled. */}
      {!loading && !error && (
        <div className="ev-verify-row">
          <div
            className={`chain-banner ${
              browser?.allOk ? "ok" : browser && !browser.supported ? "" : "bad"
            }`}
          >
            <strong>
              {browser === null
                ? "Verifying in your browser…"
                : !browser.supported
                  ? "This browser cannot verify Ed25519 signatures"
                  : browser.allOk
                    ? `${verifiedCount} of ${events.length} entries verified in this browser`
                    : `${events.length - verifiedCount} of ${events.length} entries FAILED verification`}
            </strong>
            <div className="text-muted text-sm">
              Public key <Hash value={key?.public_key} short={16} />
              {key?.key_ephemeral && (
                <span className="ev-badge ev-badge-bad" style={{ marginLeft: 8 }}>
                  ephemeral key — signatures will not survive a restart
                </span>
              )}
            </div>
          </div>
          <div className={`chain-banner ${server?.ok ? "ok" : "bad"} ev-server`}>
            <strong>
              Server&rsquo;s own check: {server?.ok ? "chain intact" : "problems reported"}
            </strong>
            <div className="text-muted text-sm">
              {server?.checked ?? 0} entries in the project chain. This is the
              platform vouching for itself; the browser check above is the one
              that does not require trusting us.
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------ */}
      {/* 1. What this deployment could not do                           */}
      {/* ------------------------------------------------------------ */}
      <section className="card ev-section">
        <h2>What this deployment could not do</h2>
        <p className="text-muted ev-lead">
          Attested at creation, from runtime state rather than configuration.
          A record that only lists capabilities is a brochure; this lists the
          degradation.
        </p>

        {!posture && !loading && (
          <div className="ev-empty">
            No posture attestation was recorded for this deployment. That is
            itself a finding: nothing here can say what the safety envelope was
            or was not able to enforce.
          </div>
        )}

        {posture && (
          <div className="ev-statements">
            <Statement
              tone={posture.connector_stub ? "warn" : "ok"}
              headline={
                posture.connector_stub
                  ? "Not a real robot"
                  : `Live ${posture.robot_type?.toUpperCase() ?? "robot"} connector`
              }
            >
              {posture.connector_stub
                ? `The ${posture.robot_type ?? ""} connector was in stub or simulated mode${
                    posture.robot_model ? ` (${posture.robot_model})` : ""
                  }. Nothing on this page is evidence about physical hardware.`
                : `Connected to a physical ${posture.robot_type ?? ""} arm${
                    posture.robot_model ? ` (${posture.robot_model})` : ""
                  }.`}
            </Statement>

            <Statement
              tone={posture.force_limiting === "enforced" ? (posture.connector_stub ? "warn" : "ok") : "bad"}
              headline={
                posture.force_limiting === "enforced"
                  ? `Force limited at ${posture.force_threshold_n ?? "?"} N${
                      posture.connector_stub ? " — in simulation only" : ""
                    }`
                  : "Force limiting NOT enforceable"
              }
            >
              {posture.force_limiting === "enforced"
                ? posture.connector_stub
                  ? "The simulated arm feeds force back, so the check genuinely acted on a number — but the number was invented by us. Do not quote this as a limit the robot keeps."
                  : "The connector reports measured contact force and the envelope acts on it."
                : "This connector does not measure contact force. The force check compares against zeros and passes on every tick regardless of what the arm is pressing on. No threshold is published because none could be kept."}
            </Statement>

            <Statement
              tone={posture.workspace_rehearsal === "enforced" ? "ok" : "bad"}
              headline={
                posture.workspace_rehearsal === "enforced"
                  ? "Motions rehearsed against the workspace box"
                  : "Workspace bounds NOT rehearsed"
              }
            >
              {posture.workspace_rehearsal === "enforced"
                ? "Every proposed action chunk was replayed against the workspace bounds before it was allowed to own the robot's future."
                : "No kinematic model was available for this arm, so proposed motions were checked for joint and velocity limits only. The workspace box was unguarded on this path."}
            </Statement>

            <Statement
              tone={posture.clamped_fields?.length ? "warn" : "ok"}
              headline={
                posture.clamped_fields?.length
                  ? `${posture.clamped_fields.length} envelope field${
                      posture.clamped_fields.length === 1 ? "" : "s"
                    } clamped server-side`
                  : "Envelope accepted as requested"
              }
            >
              {posture.clamped_fields?.length ? (
                <>
                  The client asked for more than the vendor ceiling allows on{" "}
                  {posture.clamped_fields.map((f) => (
                    <span key={f} className="ev-chip">
                      {f.replace(/_/g, " ")}
                    </span>
                  ))}
                  . The limits actually in force are the clamped ones.
                </>
              ) : (
                "Nothing requested exceeded the vendor ceiling for this robot family."
              )}
            </Statement>
          </div>
        )}
        <SignedBy entry={postureEvent} check={browser} />
      </section>

      {/* ------------------------------------------------------------ */}
      {/* 2. What the envelope refused                                    */}
      {/* ------------------------------------------------------------ */}
      <section className="card ev-section">
        <h2>What the envelope refused</h2>
        <p className="text-muted ev-lead">
          Two quantities, never summed. <strong>Model ambition</strong> is what
          the policy asked for, checked before the controller could soften it.{" "}
          <strong>Shield engagement</strong> is what the envelope actually altered
          on the way out. A metric that only reported the second one measured
          how busy the controller was and called it risk.
        </p>

        {!intervention && !loading && (
          <div className="ev-empty">
            No intervention record yet. It is signed once, when the episode
            ends — a partial count has no denominator a reader can trust.
          </div>
        )}

        {intervention?.observe_only && (
          <div className="ev-callout">
            <strong>Silent trial.</strong> This deployment ran the whole stack
            and never commanded the robot ({intervention.commands_sent ?? 0}{" "}
            commands sent). Everything below is what <em>would</em> have
            happened.
          </div>
        )}

        {intervention?.model_ambition && intervention.shield_engagement && (
          <div className="ev-channels">
            <ChannelCard
              name="Model ambition"
              subtitle="raw target vs envelope, before the PD controller"
              data={intervention.model_ambition}
              bandMax={bandMax}
            />
            <ChannelCard
              name="Shield engagement"
              subtitle="outgoing command vs envelope, as actually sent"
              data={intervention.shield_engagement}
              bandMax={bandMax}
            />
          </div>
        )}

        {intervention && (
          <div className="ev-kv ev-kv-row">
            <span className="ev-chip">held ticks {intervention.held_ticks ?? 0}</span>
            <span className="ev-chip">
              force exceedances {intervention.force_exceedances ?? 0}
            </span>
            <span className="ev-chip">
              peak force {(intervention.max_force_seen_n ?? 0).toFixed(1)} N
            </span>
            {intervention.telemetry_sha256 && (
              <span className="ev-chip" title={intervention.telemetry_sha256}>
                telemetry sha256 {intervention.telemetry_sha256.slice(0, 12)}
              </span>
            )}
            {intervention.journal_sha256 && (
              <span className="ev-chip" title={intervention.journal_sha256}>
                journal sha256 {intervention.journal_sha256.slice(0, 12)}
              </span>
            )}
          </div>
        )}
        <SignedBy entry={interventionEvent} check={browser} />
      </section>

      {/* ------------------------------------------------------------ */}
      {/* 3. Re-derive the episode                                        */}
      {/* ------------------------------------------------------------ */}
      <section className="card ev-section">
        <h2>Re-derive the episode</h2>
        <p className="text-muted ev-lead">
          Given the chunk journal and the recorded states, every command and
          every refusal follows deterministically. This rebuilds the schedule
          merge by merge and re-runs the control loop with no robot, no model
          and no network, then reports how far the result drifts from what was
          recorded. Zero is the claim.
        </p>

        <button className="sg-btn sg-btn-secondary" disabled={replaying} onClick={runReplay}>
          {replaying ? "Re-deriving…" : replay ? "Re-derive again" : "Re-derive this episode"}
        </button>

        {replayError && <div className="chain-banner bad ev-mt">{replayError}</div>}

        {replay && (
          <div className={`ev-replay ${replay.exact ? "ev-replay-exact" : "ev-replay-diverged"}`}>
            <div className="ev-replay-big">
              {replay.exact ? "divergence 0.0" : `diverged at tick ${replay.diverged_at}`}
            </div>
            <div className="ev-replay-sub">
              {replay.exact
                ? `${replay.steps_checked} steps re-derived exactly from ${replay.chunks_replayed} journalled chunks.`
                : `Re-derived command differs from the recorded one by ${replay.divergence.toExponential(2)} rad. Beyond this tick the robot's state is unknown, so the replay stops rather than extrapolating.`}
            </div>
            {replay.rejected_chunks > 0 && (
              <div className="text-muted text-sm ev-mt">
                {replay.rejected_chunks} chunk{replay.rejected_chunks === 1 ? "" : "s"} were
                refused before scheduling and are in the journal but not the replay — a
                refused plan explains a hold; replaying it would invent a future.
              </div>
            )}
            {replay.journal_truncated && (
              <div className="text-muted text-sm ev-mt">
                The journal ends mid-record. What survives was replayed.
              </div>
            )}
            {replay.notes.length > 0 && (
              <ul className="ev-notes">
                {replay.notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {/* ------------------------------------------------------------ */}
      {/* 4. Envelope as admitted                                         */}
      {/* ------------------------------------------------------------ */}
      <section className="card ev-section">
        <h2>Envelope as admitted</h2>
        <p className="text-muted ev-lead">
          What was requested, what survived the vendor ceiling, and who widened
          it. These limits are advisory: speed, force and safe-axis limiting are
          certified functions of the robot&rsquo;s own controller.
        </p>

        {envelope?.effective && (
          <div className="ev-envelope">
            <div>
              <div className="ev-label">Force threshold</div>
              <div className="ev-value">
                {envelope.effective.force_threshold_n != null
                  ? `${envelope.effective.force_threshold_n} N`
                  : "not enforceable"}
              </div>
            </div>
            <div>
              <div className="ev-label">Workspace (m)</div>
              <div className="ev-value ev-mono">
                {envelope.effective.workspace_bounds_m
                  ? ["x", "y", "z"].map((axis, i) => {
                      const lo = envelope.effective!.workspace_bounds_m![2 * i];
                      const hi = envelope.effective!.workspace_bounds_m![2 * i + 1];
                      return (
                        <div key={axis}>
                          {axis} {lo.toFixed(2)} … {hi.toFixed(2)}
                        </div>
                      );
                    })
                  : "—"}
              </div>
            </div>
            <div>
              <div className="ev-label">Velocity limits (rad/s)</div>
              <div className="ev-value ev-joints">
                {envelope.effective.velocity_limits_rad_s
                  ? envelope.effective.velocity_limits_rad_s.map((v, i) => (
                      <span key={i} className="ev-chip" title={`joint ${i + 1}`}>
                        j{i + 1} {v.toFixed(2)}
                      </span>
                    ))
                  : "—"}
              </div>
            </div>
            <div>
              <div className="ev-label">Clamped</div>
              <div className="ev-value">
                {envelope.effective.clamped_fields?.length
                  ? envelope.effective.clamped_fields.map((f) => (
                      <span key={f} className="ev-chip ev-chip-warn">
                        {f.replace(/_/g, " ")}
                      </span>
                    ))
                  : "nothing"}
              </div>
            </div>
          </div>
        )}

        {overrides.length > 0 && (
          <div className="ev-overrides">
            <div className="ev-label">Widened on the record</div>
            {overrides.map((o) => (
              <div key={o.id} className="ev-override">
                <strong>{o.actor}</strong> — {o.rationale}
                <Verified entry={o} check={browser} />
              </div>
            ))}
          </div>
        )}
        <SignedBy entry={envelopeEvent} check={browser} />
      </section>

      {/* ------------------------------------------------------------ */}
      {/* 5. Chain of custody                                             */}
      {/* ------------------------------------------------------------ */}
      <section className="card ev-section">
        <h2>Chain of custody</h2>
        <p className="text-muted ev-lead">
          Every entry about this deployment, oldest first. Each one commits to
          the hash of the one before it, so an edit anywhere breaks every link
          after it. This proves entries were not edited; it does not prove
          none were removed.
        </p>

        <ol className="ev-chain">
          {[...events].reverse().map((e, i, arr) => {
            const prev = i > 0 ? arr[i - 1] : null;
            const contiguous = prev ? e.prev_hash === prev.entry_hash : true;
            return (
              <li key={e.id} className="ev-chain-item">
                <div className="ev-chain-link">
                  <span className={`ev-chain-dot ${contiguous ? "" : "ev-chain-gap"}`} />
                </div>
                <div className="ev-chain-body">
                  <div className="ev-chain-head">
                    <span className="ev-chain-seq">#{e.seq}</span>
                    <span className="ev-chain-type">{e.event_type}</span>
                    <span className="text-muted text-sm">
                      {e.created_at ? new Date(e.created_at).toLocaleString() : "—"}
                    </span>
                    <Verified entry={e} check={browser} />
                  </div>
                  <div className="ev-chain-action">{e.action}</div>
                  <div className="ev-chain-hashes">
                    <span>
                      prev <Hash value={e.prev_hash} />
                    </span>
                    <span>
                      entry <Hash value={e.entry_hash} />
                    </span>
                    {!contiguous && (
                      <span className="text-muted text-sm">
                        · other project entries sit between these two
                      </span>
                    )}
                    <Link
                      to={APP_ROUTES.complianceEvent(projectId, e.id)}
                      className="ev-chain-open"
                    >
                      open
                    </Link>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
        {events.length === 0 && !loading && (
          <div className="ev-empty">No ledger entries reference this deployment.</div>
        )}
      </section>
    </div>
  );
}
